import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Dashboard from "@/pages/Dashboard";

/**
 * Feb 2026 -- first-login redirect to Account Setup.
 *
 * Rendered at the `/app` index route so it runs BEFORE Overview
 * mounts. Behaviour:
 *   1. Only company admins are redirected. Team admins, employees
 *      and super admins keep the existing Overview experience.
 *   2. Uses a per-company localStorage flag so the redirect fires
 *      once per browser for a given tenant. After that the manager
 *      can navigate to Overview freely.
 *   3. If the onboarding checklist call fails, we render Overview
 *      normally rather than blocking the user.
 *   4. Does not touch backend, no new endpoint, no new field. The
 *      old `sessionStorage.nextrota_setup_redirected_v1` mechanism
 *      inside `ContinueSetupBanner` is left in place as belt-and-
 *      braces so a manager who clears localStorage still gets the
 *      one-time redirect on the next fresh tab.
 */
const FLAG_PREFIX = "nextrota.setup.first_login_redirect_done.";

export default function AppIndexRedirect() {
  const { user } = useAuth();
  const [decision, setDecision] = useState(null); // "redirect" | "render" | null

  useEffect(() => {
    if (!user) { setDecision("render"); return; }
    if (user.role !== "company_admin") { setDecision("render"); return; }
    const key = `${FLAG_PREFIX}${user.company_id || ""}`;
    let already = false;
    try { already = localStorage.getItem(key) === "1"; } catch { /* ignore */ }
    if (already) { setDecision("render"); return; }
    let cancelled = false;
    api.get("/onboarding/checklist")
      .then((r) => {
        if (cancelled) return;
        const pct = Number(r.data?.percent) || 0;
        if (pct < 100) {
          try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
          setDecision("redirect");
        } else {
          setDecision("render");
        }
      })
      .catch(() => { if (!cancelled) setDecision("render"); });
    return () => { cancelled = true; };
  }, [user]);

  if (decision === null) {
    // Render nothing during the check to avoid a flash of Overview
    // for first-login managers.
    return <div data-testid="app-index-redirect-loading" />;
  }
  if (decision === "redirect") {
    return <Navigate to="/app/setup" replace />;
  }
  return <Dashboard />;
}
